<!--
  Страница службы войскового хозяйства (По ТЗ 10.09.2026).
  Роут: /household/services/:code (pfs / ovs / kes / gsm / svyaz / asu /
  rav / inzh / abts / vet / med).

  Верхняя надпись «Склад — {название службы}», под ней 5 табов:
    • Остатки      — текущие остатки на складе (сумма приходов − сумма расходов)
    • Расход       — журнал операций «Расход» по номенклатуре
    • Приход       — журнал операций «Приход»
    • Отчет        — сводный отчёт (движение + сумма стоимости)
    • В пользовании — что выдано военнослужащим (issued items)

  Табы шарят один и тот же серверный контекст `service` — на MVP используем
  оптимистичный фильтр на клиенте: фильтруем существующие эндпоинты
  wms/operations/, wms/warehouses/ по `service` (когда бэк поддержит фильтр)
  либо по `warehouse.service == code` (пока — заглушка с загрузкой).
-->
<template>
  <section class="wsp">
    <header class="wsp__head">
      <div class="wsp__title-block">
        <h2 class="wsp__title">{{ $t('household.service.warehouseTitle') }}<span v-if="service"> — {{ serviceLabel }}</span></h2>
        <div v-if="service" class="wsp__subtitle">{{ service.name_ru || service.name }}</div>
      </div>
      <div class="wsp__meta" v-if="service">
        <!-- По ТЗ 10.09.2026: у службы свой склад(ы). Если один — просто имя,
             если несколько — селект для переключения. -->
        <div v-if="warehouses.length" class="wsp__warehouse">
          <span class="wsp__warehouse-label">{{ $t('household.service.warehouseCurrent') }}:</span>
          <select v-if="warehouses.length > 1" v-model="selectedWarehouseId" @change="reloadTab">
            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
          </select>
          <span v-else class="wsp__warehouse-name">{{ warehouses[0].name }}</span>
        </div>
        <span v-else class="wsp__warehouse wsp__warehouse--empty">{{ $t('household.service.noWarehouse') }}</span>
        <span class="wsp__code">{{ service.code.toUpperCase() }}</span>
      </div>
    </header>

    <nav class="wsp__tabs">
      <button
        v-for="t in tabs"
        :key="t.key"
        class="wsp__tab"
        :class="{ 'is-active': activeTab === t.key }"
        @click="activeTab = t.key"
      >{{ $t(t.i18n) }}</button>
    </nav>

    <div class="wsp__body">
      <div v-if="loading" class="wsp__state">{{ $t('common.loadingData') }}</div>
      <div v-else-if="error" class="wsp__state wsp__state--err">{{ error }}</div>

      <!-- Остатки -->
      <div v-else-if="activeTab === 'stock'" class="wsp__panel">
        <div class="wsp__panel-head">
          <input class="wsp__search" v-model="q" type="search" :placeholder="$t('common.search')" />
        </div>
        <table class="wsp__table">
          <thead>
            <tr>
              <th>{{ $t('household.service.colName') }}</th>
              <th>{{ $t('household.service.colUnit') }}</th>
              <th class="num">{{ $t('household.service.colStock') }}</th>
              <th class="num">{{ $t('household.service.colOnHands') }}</th>
              <th class="num">{{ $t('household.service.colFree') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filteredStock.length"><td colspan="5" class="wsp__empty">{{ $t('common.noData') }}</td></tr>
            <tr v-for="row in filteredStock" :key="row.id">
              <td>{{ row.name }}</td>
              <td>{{ row.unit }}</td>
              <td class="num">{{ row.stock }}</td>
              <td class="num">{{ row.on_hands }}</td>
              <td class="num"><b>{{ row.free }}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Расход -->
      <div v-else-if="activeTab === 'expense'" class="wsp__panel">
        <div class="wsp__panel-head">
          <input class="wsp__search" v-model="q" type="search" :placeholder="$t('common.search')" />
          <button class="btn btn--primary" @click="onOpenAddExpense">+ {{ $t('household.service.addExpense') }}</button>
        </div>
        <OperationsList :rows="filteredExpenses" kind="expense" />
      </div>

      <!-- Приход -->
      <div v-else-if="activeTab === 'receipt'" class="wsp__panel">
        <div class="wsp__panel-head">
          <input class="wsp__search" v-model="q" type="search" :placeholder="$t('common.search')" />
          <button class="btn btn--primary" @click="onOpenAddReceipt">+ {{ $t('household.service.addReceipt') }}</button>
        </div>
        <OperationsList :rows="filteredReceipts" kind="receipt" />
      </div>

      <!-- Отчет -->
      <div v-else-if="activeTab === 'report'" class="wsp__panel">
        <div class="wsp__filters">
          <label>{{ $t('household.service.from') }}: <input type="date" v-model="reportFrom" @change="onReloadReport" /></label>
          <label>{{ $t('household.service.to') }}: <input type="date" v-model="reportTo" @change="onReloadReport" /></label>
          <button class="btn btn--secondary" @click="onReloadReport">↻</button>
          <button class="btn btn--secondary" @click="onExportReport">{{ $t('common.export') }}</button>
        </div>
        <table class="wsp__table">
          <thead>
            <tr>
              <th>{{ $t('household.service.colName') }}</th>
              <th>{{ $t('household.service.colUnit') }}</th>
              <th class="num">{{ $t('household.service.colOpening') }}</th>
              <th class="num">{{ $t('household.service.colReceiptSum') }}</th>
              <th class="num">{{ $t('household.service.colExpenseSum') }}</th>
              <th class="num">{{ $t('household.service.colWriteOff') }}</th>
              <th class="num">{{ $t('household.service.colClosing') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!report.length"><td colspan="7" class="wsp__empty">{{ $t('common.noData') }}</td></tr>
            <tr v-for="row in report" :key="row.id">
              <td>{{ row.name }}</td>
              <td>{{ row.unit }}</td>
              <td class="num">{{ row.opening }}</td>
              <td class="num">{{ row.receipt }}</td>
              <td class="num">{{ row.expense }}</td>
              <td class="num">{{ row.write_off }}</td>
              <td class="num"><b>{{ row.closing }}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- В пользовании -->
      <div v-else-if="activeTab === 'issued'" class="wsp__panel">
        <div class="wsp__panel-head">
          <input class="wsp__search" v-model="q" type="search" :placeholder="$t('common.search')" />
          <button class="btn btn--primary" @click="onOpenAddIssue">+ {{ $t('household.service.addIssue') }}</button>
        </div>
        <table class="wsp__table">
          <thead>
            <tr>
              <th>{{ $t('household.service.colHolder') }}</th>
              <th>{{ $t('household.service.colName') }}</th>
              <th>{{ $t('household.service.colSerial') }}</th>
              <th class="num">{{ $t('household.service.colQty') }}</th>
              <th>{{ $t('household.service.colIssuedAt') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filteredIssued.length"><td colspan="6" class="wsp__empty">{{ $t('common.noData') }}</td></tr>
            <tr v-for="row in filteredIssued" :key="row.id">
              <td>{{ row.holder }}</td>
              <td>{{ row.name }}</td>
              <td>{{ row.serial_number || '—' }}</td>
              <td class="num">{{ row.quantity }} {{ row.unit || '' }}</td>
              <td>{{ fmtDate(row.issued_at) }}</td>
              <td>
                <button class="btn-inline" @click="onReturn(row)">{{ $t('household.service.actionReturn') }}</button>
                <button class="btn-inline btn-inline--danger" @click="onWriteOff(row)">{{ $t('household.service.actionWriteOff') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Модалки -->
    <OperationFormModal
      v-if="showOpModal"
      :service="service"
      :kind="opModalKind"
      :warehouse-id="selectedWarehouseId"
      @close="showOpModal = false"
      @saved="onOpSaved"
    />
    <IssueForUseModal
      v-if="showIssueModal"
      :service="service"
      :warehouse-id="selectedWarehouseId"
      @close="showIssueModal = false"
      @saved="onIssueSaved"
    />
  </section>
</template>

<script>
import { getServiceByCode } from '@/api/services'
import {
  getServiceStock, getServiceReport, getServiceIssued,
  listOperationsByService, returnIssue, writeOffIssue,
  listWarehousesByService,
} from '@/api/wmsService'
import OperationsList from './components/OperationsList.vue'
import IssueForUseModal from './components/IssueForUseModal.vue'
import OperationFormModal from './components/OperationFormModal.vue'

export default {
  name: 'WarehouseServicePage',
  components: { OperationsList, IssueForUseModal, OperationFormModal },
  data() {
    return {
      loading: false,
      error: null,
      service: null,
      activeTab: 'stock',
      q: '',
      warehouses: [],           // склады этой службы
      selectedWarehouseId: null, // активный (если >1)
      stock: [],
      expenses: [],
      receipts: [],
      issued: [],
      report: [],
      reportFrom: this.defaultFromISO(),
      reportTo: this.todayISO(),
      // Модалки
      showIssueModal: false,
      showOpModal: false,
      opModalKind: 'receipt',   // 'receipt' | 'expense'
    }
  },
  computed: {
    tabs() {
      return [
        { key: 'stock',   i18n: 'household.service.tabStock' },
        { key: 'expense', i18n: 'household.service.tabExpense' },
        { key: 'receipt', i18n: 'household.service.tabReceipt' },
        { key: 'report',  i18n: 'household.service.tabReport' },
        { key: 'issued',  i18n: 'household.service.tabIssued' },
      ]
    },
    serviceLabel() {
      if (!this.service) return ''
      const kk = this.$i18n?.locale === 'kz'
      return kk ? (this.service.name_kk || this.service.name) : (this.service.short_name || this.service.name)
    },
    // Единая маска поиска для всех табов — MVP-фильтр
    matches() {
      const q = (this.q || '').trim().toLowerCase()
      if (!q) return () => true
      return (row) => Object.values(row || {}).some(v =>
        String(v ?? '').toLowerCase().includes(q))
    },
    filteredStock()    { return this.stock.filter(this.matches) },
    filteredExpenses() { return this.expenses.filter(this.matches) },
    filteredReceipts() { return this.receipts.filter(this.matches) },
    filteredIssued()   { return this.issued.filter(this.matches) },
  },
  watch: {
    // При смене :code (переход между службами в сайдбаре) — перегружаем данные.
    '$route.params.code': { immediate: false, handler() { this.load() } },
    // При смене таба — тянем только то, что нужно.
    activeTab() { this.reloadTab() },
  },
  async mounted() {
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true; this.error = null
      const code = this.$route.params.code
      try {
        this.service = await getServiceByCode(code)
        if (!this.service) {
          this.error = this.$t('household.service.notFound', { code })
          return
        }
        // Тянем склады службы. По seed'у их 1 на службу, но UI поддерживает N.
        this.warehouses = await listWarehousesByService(code)
        this.selectedWarehouseId = this.warehouses[0]?.id || null
        await this.reloadTab()
      } catch (e) {
        this.error = e?.message || 'Load failed'
      } finally { this.loading = false }
    },
    async reloadTab() {
      const code = this.$route.params.code
      try {
        if (this.activeTab === 'stock') {
          const { items } = await getServiceStock(code)
          this.stock = items || []
        } else if (this.activeTab === 'expense') {
          this.expenses = (await listOperationsByService(code, 'expense')).map(this.mapOp)
        } else if (this.activeTab === 'receipt') {
          this.receipts = (await listOperationsByService(code, 'receipt')).map(this.mapOp)
        } else if (this.activeTab === 'report') {
          const { items } = await getServiceReport(code, this.reportFrom, this.reportTo)
          this.report = items || []
        } else if (this.activeTab === 'issued') {
          this.issued = (await getServiceIssued(code)).map(i => ({
            id: i.id,
            holder: i.holder_name || `#${i.holder}`,
            name: i.nomenclature_name,
            unit: i.nomenclature_unit,
            serial_number: i.serial_number,
            quantity: i.quantity,
            issued_at: i.issued_at,
            _raw: i,
          }))
        }
      } catch (e) { console.error('reloadTab', e); this.error = e?.message || 'Load failed' }
    },
    // Плоский маппинг Operation → строка журнала (для OperationsList).
    mapOp(op) {
      const first = (op.nomenclatures || [])[0] || {}
      return {
        id: op.id,
        date: op.date,
        name: first.nomenclature_name || first.nomenclature?.name || '—',
        quantity: first.quantity,
        unit: first.nomenclature?.unit || '',
        counterparty: op.counterparty_name || '',
        comment: op.description || op.expense_subtype_label || '',
      }
    },
    todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` },
    defaultFromISO() { const d = new Date(); d.setDate(d.getDate() - 30); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` },
    async onReloadReport() { await this.reloadTab() },

    onOpenAddExpense() { this.opModalKind = 'expense'; this.showOpModal = true },
    onOpenAddReceipt() { this.opModalKind = 'receipt'; this.showOpModal = true },
    onOpenAddIssue()   { this.showIssueModal = true },
    async onOpSaved()  { this.showOpModal = false; await this.reloadTab() },
    async onIssueSaved() { this.showIssueModal = false; await this.reloadTab() },

    async onReturn(row) {
      if (!confirm(this.$t('household.service.confirmReturn'))) return
      try { await returnIssue(row.id); await this.reloadTab() }
      catch (e) { alert(e?.message || 'Ошибка'); console.error(e) }
    },
    async onWriteOff(row) {
      if (!confirm(this.$t('household.service.confirmWriteOff'))) return
      try { await writeOffIssue(row.id, { reason: 'Списание с рук пользователя' }); await this.reloadTab() }
      catch (e) { alert(e?.message || 'Ошибка'); console.error(e) }
    },
    fmtDate(iso) {
      if (!iso) return ''
      const [y, m, d] = String(iso).split('-')
      return `${d}.${m}.${y}`
    },
    onExportReport() {
      // TODO: xlsx-экспорт отчёта в формате службы (советская форма).
      alert(this.$t('common.notImplementedYet'))
    },
  },
}
</script>

<style scoped>
.wsp { padding: 20px 24px; }
.wsp__head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 20px; margin-bottom: 16px; flex-wrap: wrap;
}
.wsp__title-block { display: flex; flex-direction: column; }
.wsp__title { margin: 0; font-size: 22px; font-weight: 800; color: #111827; }
.wsp__subtitle { margin-top: 4px; font-size: 13px; color: #6B7280; }
.wsp__code {
  display: inline-block; padding: 3px 10px; background: #EFF6FF;
  color: #1E3A8A; border: 1px solid #93C5FD; border-radius: 999px;
  font-weight: 700; font-size: 12px; letter-spacing: 0.5px;
}
.wsp__meta { display: flex; align-items: center; gap: 12px; }
.wsp__warehouse { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #374151; }
.wsp__warehouse-label { color: #6B7280; font-weight: 500; }
.wsp__warehouse-name { font-weight: 600; color: #111827; }
.wsp__warehouse select { padding: 4px 8px; border: 1px solid #D1D5DB; border-radius: 6px; font-size: 13px; }
.wsp__warehouse--empty { color: #B91C1C; font-style: italic; }

.wsp__tabs {
  display: flex; gap: 4px; border-bottom: 1px solid #E5E7EB; margin-bottom: 16px;
  overflow-x: auto;
}
.wsp__tab {
  padding: 10px 18px; border: 0; background: none; cursor: pointer;
  font-family: inherit; font-size: 14px; font-weight: 600; color: #6B7280;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
  transition: color 0.1s, border-color 0.1s;
  white-space: nowrap;
}
.wsp__tab:hover { color: #111827; }
.wsp__tab.is-active { color: #2F78EE; border-bottom-color: #2F78EE; }

.wsp__body { min-height: 200px; }
.wsp__state { padding: 40px; text-align: center; color: #6B7280; }
.wsp__state--err { color: #DC2626; }

.wsp__panel { background: #fff; border: 1px solid #E5E7EB; border-radius: 10px; padding: 16px; }
.wsp__panel-head { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; flex-wrap: wrap; }
.wsp__search {
  flex: 0 1 320px; padding: 8px 12px; border: 1px solid #D1D5DB;
  border-radius: 8px; font-family: inherit; font-size: 13px;
}
.wsp__filters {
  display: flex; gap: 14px; align-items: center; margin-bottom: 14px; flex-wrap: wrap;
}
.wsp__filters label { font-size: 13px; color: #374151; }
.wsp__filters input[type="date"] { margin-left: 6px; padding: 4px 8px; border: 1px solid #D1D5DB; border-radius: 6px; }

.wsp__table { width: 100%; border-collapse: collapse; font-size: 13px; }
.wsp__table th, .wsp__table td { padding: 8px 12px; border-bottom: 1px solid #F3F4F6; text-align: left; }
.wsp__table th { background: #F9FAFB; font-weight: 600; color: #374151; }
.wsp__table td.num, .wsp__table th.num { text-align: right; font-variant-numeric: tabular-nums; }
.wsp__empty { text-align: center; color: #9CA3AF; padding: 20px; }

.btn { padding: 8px 14px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; }
.btn--primary { background: #2F78EE; color: #fff; }
.btn--primary:hover { background: #1D4ED8; }
.btn--secondary { background: #fff; border-color: #D1D5DB; color: #374151; }
.btn--secondary:hover { background: #F3F4F6; }
.btn-inline {
  padding: 4px 10px; border-radius: 6px; border: 1px solid #D1D5DB;
  background: #fff; cursor: pointer; font-size: 12px; margin-right: 4px;
  font-family: inherit;
}
.btn-inline:hover { background: #F3F4F6; }
.btn-inline--danger { color: #B91C1C; border-color: #FCA5A5; }
.btn-inline--danger:hover { background: #FEE2E2; }
</style>
